/**
 * SpecialistCard — Unit 5 of the listing-conversion overhaul.
 *
 * Reusable trust card rendered inside the LeadForm (Unit 4 + Unit 8 will
 * mount it) and potentially as a standalone section on Broker/Traditional/
 * Fleet variants. v1 always renders Fernando Ortega.
 *
 * The component reads its data from the client-side `SPECIALISTS` registry
 * (`src/constants/specialists.ts`) but accepts a `serverOverride` prop so
 * Unit 14 can swap in `Location.specialist` from the server without a
 * downstream refactor.
 *
 * Editorial vocabulary per `spotme-client/CLAUDE.md`:
 *   - hairline border + cream/white surfaces, never drop shadows
 *   - `text-ink-700` primary text, `text-ink-200` muted
 *   - Fraunces eyebrow in `text-blue-500`
 */

import { useTranslation } from 'next-i18next'

import { buildLeadFormWaMessage } from 'src/components/LeadForm/buildLeadFormWaMessage'
import ResponseBadge from 'src/components/LeadForm/ResponseBadge'
import { getSpecialist } from 'src/constants/specialists'
import { useClickIdsForPrefill } from 'src/hooks/useClickIdsForPrefill'
import { useWaSessionToken } from 'src/hooks/useWaSessionToken'
import { SpecialistKey } from 'src/lib/listingVariants/types'
import type { IConfiguration, ILocation } from 'src/types/graphql'
import { aliasWaSession } from 'src/utils/waSessionToken'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

/**
 * Subset of LeadForm state we surface in the WA prefill. Matches the
 * `values` shape in `buildLeadFormWaMessage` so the listing-page mount
 * (User.tsx — passes URL params) and the in-form mount (LeadForm/index.tsx
 * — passes `state.values`) can both feed this card without a translation
 * layer.
 */
type SpecialistWaContextValues = {
  name?: string
  sqm?: number | null
  quantity?: number | null
  when?: string | null
  additionalInfo?: string
}

export interface SpecialistCardProps {
  specialistKey: SpecialistKey
  /**
   * When provided, overrides the static client registry (Unit 14's
   * `Location.specialist` server payload). Pass `null` explicitly to fall
   * back to the client constant. The server payload still owns routing
   * (WhatsApp number, HubSpot owner) — we just use `name` and `photoUrl`
   * for display.
   */
  serverOverride?: {
    name: string
    photoUrl: string
  } | null
  /**
   * Whether to reserve a slot for the `<ResponseBadge />` (Unit 6).
   * Unit 8 will wire the actual badge — for now the slot is a TODO marker.
   */
  showResponseBadge?: boolean
  /**
   * `compact` fits inside a LeadForm step (48px photo, condensed type).
   * `inline` is wider for a page-section mount (64px photo, base type).
   */
  layout?: 'compact' | 'inline'
  /**
   * Listing context for the WhatsApp prefill. When `location` is present,
   * the WA button opens with a rich prefill — intro + listing URL + ref
   * + any captured qualifying values. Without it, falls back to the static
   * `SPECIALIST.WA_PREFILL` locale string (last resort; specialist routing
   * loses all listing signal).
   */
  location?: ILocation | null
  configuration?: IConfiguration | null
  /**
   * Qualifier values to fold into the WA prefill. The listing surface
   * passes `when` (from `?storageTime=`) and `quantity` (from `?qty=`);
   * the in-form mount passes the live `state.values` so the message
   * reflects whatever the renter has just typed.
   */
  contextValues?: SpecialistWaContextValues
  /**
   * Editorial tone for the card chrome.
   *   - `'specialist'` (default) → "Tu asesor" framing. Fits Broker +
   *     Fleet where the deal genuinely starts as a scope conversation.
   *   - `'concierge'` → "Te conectamos" framing. Fits Traditional B2C
   *     self-storage where calling the specialist an "asesor" overstates
   *     the friction — the renter just wants a unit, not a broker.
   *   - `'supply'` → "Tu contacto" framing for host-acquisition pages
   *     (`/anfitrion/tradicional`, `/anfitrion/negocio`). Audience is a
   *     property owner asking "will SpotMe bring me renters?" — the
   *     specialist is their onboarding partner, not their broker.
   * The photo, name, response badge, and WA routing are identical; only
   * the eyebrow + subtitle copy switches.
   */
  tone?: 'specialist' | 'concierge' | 'supply'
  /**
   * Fired when the renter clicks the WA pill on this card. Callers wire
   * this to `useAddToCart('contact_wa', …)` on demand surfaces and to
   * `useSupplyLead({ funnel, source: 'specialist_wa' })` on supply
   * surfaces so the click hits GA4 / Meta / UET / LinkedIn + the internal
   * ES event with the right segmentation. Without this prop the click
   * still opens WA via the `<a href>` — it just won't appear in pixel
   * conversion tracking.
   */
  onWhatsappClick?: () => void
  /**
   * When `tone='supply'`, selects the funnel-specific WA prefill so the
   * LangGraph chatbot can tag the incoming WhatsApp message with the
   * correct `customerType` on the first turn (vs. having to ask a
   * clarifying question). Maps to `SPECIALIST.SUPPLY.WA_PREFILL_*`
   * locale keys:
   *   - `'traditional'` → "Hola, soy operador de self-storage..."
   *     (Anexo C / SpotMe Traditional program). Used on
   *     `/anfitrion/tradicional`.
   *   - `'business'` → "Hola, tengo un espacio comercial..."
   *     (SpotMe Negocio / B2B host funnel). Used on `/anfitrion/negocio`.
   *   - `'parking'` → "Hola, soy operador de estacionamiento..."
   *     (Anexo C parking operators — Traditional lead-gen). Used on
   *     `/anfitrion/estacionamiento`. Selects only the WA prefill; the
   *     analytics funnel stays `'traditional'` (no new supply funnel).
   * When omitted, falls back to the generic `WA_PREFILL` for `tone='supply'`.
   * Ignored for non-supply tones.
   */
  supplyFunnel?: 'traditional' | 'business' | 'parking'
}

const WHATSAPP_PREFILL_KEY = 'SPECIALIST.WA_PREFILL' as const

function resolveSpecialistData(
  specialistKey: SpecialistKey,
  serverOverride: SpecialistCardProps['serverOverride']
): { name: string; photoUrl: string; hasWhatsapp: boolean } | null {
  if (serverOverride) {
    return { ...serverOverride, hasWhatsapp: true }
  }
  const fromRegistry = getSpecialist(specialistKey)
  if (!fromRegistry) return null
  return {
    name: fromRegistry.name,
    photoUrl: fromRegistry.photoUrl,
    hasWhatsapp: fromRegistry.hasWhatsapp
  }
}

export default function SpecialistCard({
  specialistKey,
  serverOverride = null,
  showResponseBadge = false,
  layout = 'compact',
  location = null,
  configuration = null,
  contextValues,
  tone = 'specialist',
  onWhatsappClick,
  supplyFunnel
}: SpecialistCardProps) {
  const { t, i18n } = useTranslation('details')
  // Mount-gated click IDs for the WA href (empty on SSR + first render so the
  // href doesn't drift → #418). Must sit ABOVE the early returns below to obey
  // the Rules of Hooks. Consumed in the waMessage build further down.
  const waClickIds = useClickIdsForPrefill()
  // Same deal: mount-gated (SSR'd href) AND PostHog-ready (a render-time
  // `sessionId()` read is always `''` behind the 2 s fuse). Also ABOVE the
  // early returns — Rules of Hooks. Consumed in the waMessage build and the
  // WA onClick further down.
  const waSessionToken = useWaSessionToken()

  const specialist = resolveSpecialistData(specialistKey, serverOverride)
  if (!specialist) return null
  // Half-configured specialist (registry entry exists but no photo yet —
  // e.g. a newly added specialist before their S3 asset lands). Render
  // nothing rather than ship a broken-image card.
  if (!specialist.photoUrl) return null

  let eyebrowText: string
  let subtitleText: string
  if (tone === 'supply') {
    eyebrowText = t('SPECIALIST.SUPPLY.EYEBROW')
    subtitleText = t('SPECIALIST.SUPPLY.SUBTITLE')
  } else if (tone === 'concierge') {
    eyebrowText = t('SPECIALIST.CONCIERGE.EYEBROW')
    subtitleText = t('SPECIALIST.CONCIERGE.SUBTITLE')
  } else {
    eyebrowText = t('SPECIALIST.EYEBROW')
    subtitleText = t('SPECIALIST.SUBTITLE')
  }

  // Rich WA prefill when listing context is present. Without `location`,
  // the helper would emit just the intro line — at that point a static
  // locale string reads better (it's been copywritten as a standalone
  // sentence). Supply pages have no `location`, and the demand prefill
  // ("me interesa este espacio") would mis-frame the conversation as a
  // renter inquiry on a host-acquisition page — swap in the supply
  // variant, funnel-specific when caller knows which page they're on.
  // The funnel-specific strings match analysis.ts host-side examples so
  // the LangGraph `analyze` node tags `customerType=HOST_CORPORATE`
  // (Anexo C operator or B2B host) on the first turn, instead of having
  // to ask a clarifying question.
  // Narrow union of literal i18n keys so the typed `t()` accepts the
  // value — `let key: string = ...` would widen and break strict t() typing.
  const fallbackPrefillKey:
    | typeof WHATSAPP_PREFILL_KEY
    | 'SPECIALIST.SUPPLY.WA_PREFILL'
    | 'SPECIALIST.SUPPLY.WA_PREFILL_TRADITIONAL'
    | 'SPECIALIST.SUPPLY.WA_PREFILL_BUSINESS'
    | 'SPECIALIST.SUPPLY.WA_PREFILL_PARKING' =
    tone === 'supply'
      ? supplyFunnel === 'traditional'
        ? 'SPECIALIST.SUPPLY.WA_PREFILL_TRADITIONAL'
        : supplyFunnel === 'business'
          ? 'SPECIALIST.SUPPLY.WA_PREFILL_BUSINESS'
          : supplyFunnel === 'parking'
            ? 'SPECIALIST.SUPPLY.WA_PREFILL_PARKING'
            : 'SPECIALIST.SUPPLY.WA_PREFILL'
      : WHATSAPP_PREFILL_KEY
  // `waSessionToken` (read above the early returns) rides the prefill as
  // `?sid={token}` so the backend can tie the inbound WhatsApp thread back to
  // this browser session. When it's empty the helper omits it and the backend
  // falls back to phone-based attribution via `findWhatsappAttribution`.
  const waMessage = location
    ? buildLeadFormWaMessage({
        location,
        configuration,
        values: contextValues ?? {},
        locale: i18n.language === 'en' ? 'en' : 'es',
        sessionToken: waSessionToken,
        clickIds: waClickIds
      })
    : t(fallbackPrefillKey)

  const isInline = layout === 'inline'
  // Inline (listing-page section): 56px on mobile, 64px on desktop.
  // Mobile is tighter horizontally — a 64px photo + gap-3 + name + response
  // badge crowded the row, forcing the badge to wrap. Compact (LeadForm
  // step header): stays at 48px on both breakpoints. Sizing via className
  // only (Trigger D Phase 4 background-image div — no inline width/height).
  const photoClass = isInline ? 'size-14 md:size-16' : 'size-12'
  const nameClass = isInline
    ? 'text-base font-semibold text-ink-700'
    : 'text-[15px] font-semibold text-ink-700'

  // Chrome differs by surface:
  //   - `inline` (listing-page section): borderless + no bg. The parent
  //     `<section>` in User.tsx renders the `border-t` divider above
  //     and provides `pt-8`. Adding the card's own 4-sided border here
  //     stacked TWO horizontal hairlines (section divider + card top
  //     edge) only ~32px apart — read as a double separator between
  //     Map and Fernando on Traditional/Broker/Fleet listings.
  //   - `compact` (LeadForm step header): keep the card chrome since
  //     the LeadForm drawer doesn't render a divider above the card.
  const containerClass = isInline
    ? ''
    : 'border border-ink-100/15 bg-white-500 p-4'

  return (
    <div className={containerClass}>
      <div className="flex items-start gap-3">
        {/*
          CSS background-image div instead of <img>. Two-pass escalation:
          - Pass 1 (2026-05-30): migrated next/image to plain <img>. Killed
            the style="color:transparent" SSR drift but plain <img> still
            fired Phase 3 attribute-order drift on `src` (SSR src-first vs
            CSR src-last). Confirmed via at-error captureHydrationDiff on
            /pension-estacionamiento/flotillas multiple times (Chrome Mobile
            WebView Android, FB IAB iOS, Telcel/izzi Mexico, 2026-06-01).
          - Pass 2 (2026-06-01): removed <img> entirely. Photo is a
            decorative trust face (specialist's name appears in the H2
            below) - background-image div with role="img" aria-label
            preserves a11y semantics without an <img> element. But the
            inline `background-image: url(...)` still drifts: SSR emits
            `url(https://...)` (no quotes) and the browser's CSSOM
            normalizes the live DOM attribute to `url("https://...")`
            (adds quotes + space). React 19 compares its serialized style
            against the DOM attribute byte-for-byte → #418. Confirmed still
            firing on /flotillas for 27 real users (PostHog 2026-06-03).
          - Pass 3 (2026-06-03, this): `suppressHydrationWarning`. This is
            a GENUINE React-tree style-attribute mismatch (not third-party
            DOM mutation like the logo's failed Pass 4), so suppress is the
            correct escape hatch: React keeps the SSR DOM, skips the
            clientRender fallback, emits NO recoverable error → both our
            Datadog recorder and PostHog's console.error autocapture go
            quiet, AND the 27 users stop getting a fallback. The computed
            style is identical (same image renders); only the attribute
            string representation differed. See CLAUDE.md "Trigger D Phase
            5 - suppressHydrationWarning for genuine inline-style drift".
        */}
        <div
          role="img"
          aria-label={specialist.name}
          className={`${photoClass} shrink-0 rounded-full bg-cover bg-center bg-no-repeat`}
          style={{ backgroundImage: `url("${specialist.photoUrl}")` }}
          suppressHydrationWarning
        />
        <div className="min-w-0 flex-1">
          <p className="font-display text-[10px] tracking-[0.22em] text-blue-500 uppercase">
            {eyebrowText}
          </p>
          <p className={`mt-1 ${nameClass}`}>{specialist.name}</p>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-200">
            {subtitleText}
          </p>
          {showResponseBadge && (
            <div className="mt-2">
              <ResponseBadge />
            </div>
          )}
        </div>
      </div>

      {specialist.hasWhatsapp && (
        // ⚠️ v1 routing — the specialist's face is NOT their phone.
        //
        // What this button does TODAY:
        //   - The avatar, name, "En línea ahora" badge, and "Respondo en
        //     <30 min" pill are Fernando (or whoever serverOverride passes
        //     in). That's the TRUST SIGNAL — the human face the renter
        //     should expect to talk to.
        //   - The WA URL itself is `buildWhatsappUrl(waMessage)` with NO
        //     `recipientNumber` argument, so it falls back to
        //     `SPOTME_WHATSAPP_NUMBER` (the SpotMe corporate WhatsApp,
        //     +52 81 1821 6319 → src/utils/whatsapp.ts). The conversation
        //     lands in the LangGraph chatbot's inbox, not Fernando's
        //     personal phone.
        //   - The chatbot triages spam / out-of-scope, qualifies the lead,
        //     creates the HubSpot deal, and routes ownership to the
        //     correct specialist via existing HubSpot owner logic. The
        //     human-to-human conversation handoff happens AFTER the
        //     chatbot has done its work.
        //
        // Why this shape:
        //   - It closes the server-side `qualified_whatsapp_lead` loop
        //     (LangGraph `complete` node → captureServerEvent +
        //     sendConversionEvent to Google Ads / Meta CAPI). Direct
        //     wa.me to Fernando bypasses this — Smart Bidding loses its
        //     deepest signal and HubSpot misses the analysis payload.
        //   - It protects specialists from leaked personal phones in any
        //     public surface (listing pages, /reservar, screenshots, etc).
        //   - It funnels every inbound through one number so triage,
        //     load-balancing, and the per-thread `qualifiedLeadEventSent`
        //     dedupe all live in one place.
        //
        // `specialist.whatsappE164` (queried via `reservarContact` and
        // populated server-side from the HubSpot OWNERS array) is THE
        // EVENTUAL DESTINATION the server already knows — we just don't
        // route to it from the client yet. Today the field is only used
        // server-side for `findWhatsappAttribution` (matching inbound WA
        // threads back to a specialist for HubSpot owner assignment) and
        // for `hasWhatsapp` gating here.
        //
        // To switch to direct-to-specialist routing later (when sales-ops
        // is provisioned to absorb un-triaged inbound):
        //   1. Plumb `specialist.whatsappE164` (or its serverOverride
        //      equivalent) into this component as a prop.
        //   2. Pass it as the 2nd arg to buildWhatsappUrl:
        //        href={buildWhatsappUrl(waMessage, specialist.whatsappE164)}
        //   3. Reproduce the qualified_whatsapp_lead loop client-side
        //      (fire on click) OR accept the analytics regression for the
        //      specialist-direct surfaces only.
        //   4. Update the WhatsApp routing rule in spotme-client/CLAUDE.md
        //      ("WhatsApp routing — trust face vs destination number") to
        //      reflect the new policy. Mirror to the .cursor rule.
        //
        // DO NOT route directly to Fer's personal phone "just for this
        // surface" without doing 1-4 above. The whole point of the v1
        // shape is the analytics + safety contract; bypassing it on one
        // surface fragments the contract silently.
        <a
          href={buildWhatsappUrl(waMessage)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            // Alias the WA identity onto this browser person BEFORE handing
            // off, so the server's later `qualified_whatsapp_lead` (fired
            // with `distinctId: session:{token}`) merges into this person
            // instead of spawning a second one and double-counting the lead.
            //
            // Fired here rather than in each parent's `onWhatsappClick`
            // because that prop is optional and has 8+ call sites — one
            // place beats eight that can drift. Guarded on `waSessionToken`
            // so it only fires when the prefill actually carries the `sid`
            // (supply-page mounts pass `location: null` → no token → the
            // server could never read it back, so an alias there would
            // identify host-side clickers for nothing).
            aliasWaSession(waSessionToken)
            onWhatsappClick?.()
          }}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-ink-100/15 bg-green-500 px-5 py-2.5 text-[13px] font-semibold text-white-500 transition-colors hover:bg-green-600"
        >
          {/* Inline SVG instead of <Image src={WhatsappIcon}>. Same Pass 5
              reasoning as RadioButton / Checkbox - next/image's
              color:transparent style injection drifts on SSR/CSR. Plain <img>
              has residual attribute-order drift. Inline SVG has neither
              surface. See CLAUDE.md "Trigger D Phase 3 - canonical inline SVG". */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M11.61 9.58803C11.4113 9.48869 10.4373 9.01003 10.256 8.94336C10.0747 8.87736 9.94267 8.84469 9.81 9.04336C9.678 9.24069 9.29867 9.68736 9.18333 9.81936C9.06733 9.95203 8.952 9.96803 8.754 9.86936C8.556 9.76936 7.91733 9.56069 7.16067 8.88603C6.572 8.36069 6.174 7.71203 6.05867 7.51336C5.94333 7.31536 6.046 7.20803 6.14533 7.10936C6.23467 7.02069 6.34333 6.87803 6.44267 6.76269C6.542 6.64669 6.57467 6.56403 6.64067 6.43136C6.70733 6.29936 6.674 6.18403 6.624 6.08469C6.57467 5.98536 6.17867 5.01003 6.01333 4.61336C5.85267 4.22736 5.68933 4.28003 5.568 4.27336C5.452 4.26803 5.32 4.26669 5.188 4.26669C5.056 4.26669 4.84133 4.31603 4.66 4.51469C4.478 4.71269 3.96667 5.19203 3.96667 6.16736C3.96667 7.14203 4.676 8.08403 4.77533 8.21669C4.87467 8.34869 6.172 10.35 8.15933 11.208C8.63267 11.412 9.00133 11.534 9.28867 11.6247C9.76333 11.776 10.1953 11.7547 10.5367 11.7034C10.9167 11.6467 11.7087 11.224 11.874 10.7614C12.0387 10.2987 12.0387 9.90203 11.9893 9.81936C11.94 9.73669 11.808 9.68736 11.6093 9.58803H11.61ZM7.99533 14.5234H7.99267C6.81229 14.5236 5.65358 14.2063 4.638 13.6047L4.398 13.462L1.90333 14.1167L2.56933 11.6847L2.41267 11.4354C1.75276 10.3849 1.40353 9.16921 1.40533 7.92869C1.40667 4.29536 4.36267 1.33936 7.998 1.33936C9.758 1.33936 11.4127 2.02603 12.6567 3.27136C13.2703 3.88241 13.7566 4.60907 14.0875 5.40929C14.4185 6.20951 14.5874 7.06741 14.5847 7.93336C14.5833 11.5667 11.6273 14.5234 7.99533 14.5234ZM13.6033 2.32536C12.8688 1.58598 11.9948 0.999718 11.0321 0.600561C10.0693 0.201403 9.03689 -0.0027175 7.99467 2.73184e-05C3.62533 2.73184e-05 0.068 3.55669 0.0666667 7.92803C0.064642 9.31919 0.429583 10.6863 1.12467 11.8914L0 16L4.20267 14.8974C5.36525 15.5308 6.66806 15.8627 7.992 15.8627H7.99533C12.3647 15.8627 15.922 12.306 15.9233 7.93403C15.9266 6.89221 15.7232 5.86009 15.325 4.89738C14.9267 3.93467 14.3416 3.06047 13.6033 2.32536Z"
              fill="white"
            />
          </svg>
          {t('SPECIALIST.WA_CTA')}
        </a>
      )}
    </div>
  )
}
